import { useState, useEffect, ChangeEvent } from 'react';
import { useTitle, useUnmount } from 'react-use';
import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';

import { IKnmSearchClipped } from 'ApiPackage/SearchModule/interfaces/IKnmSearchClipped';
import { exportInOds, exportInXlsx } from 'ApiPackage/SearchModule/services/search';
import { captchaSetActionAfterTokenLoad, captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { coreHideNotices } from 'CorePackage/MainModule/actions/coreActions';
import { composeValidators } from 'CorePackage/MainModule/common/composeValidators';
import { Layout } from 'CorePackage/MainModule/components/Layout';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/makeMaxCharactersValidator';
import { makeMinCharactersValidator } from 'CorePackage/MainModule/validators/makeMinCharactersValidator';
import { Button } from 'UIPackage/MainModule/components/Button';
import { DropdownButton } from 'UIPackage/MainModule/components/DropdownButton';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';

import { IKnmSearchInitFilter } from '../interfaces/IKnmSearchInitFilter';
import { getSearchFilter, getSearchResultsData, getSearchString } from '../selectors/searchSelectors';
import { SearchFiltersModal } from './SearchFiltersModal';
import { SearchResults } from './SearchResults';
import { SearchFilters } from './SearchFilters';
import { EXPORT_CHECK_LIST_ODS_FILE_NAME, EXPORT_CHECK_LIST_XLSX_FILE_NAME } from '../constants/searchExportFileNames';
import styles from './Search.module.scss';
import { initialSearchTableColumns } from '../constants/initialSearchTableColumns';
import {
  searchExportData,
  searchGetResultData,
  searchGetResultDataNextPage,
  searchResetPage,
  searchSetOrder,
  searchSetSearchString,
} from '../actions/searchActions';

interface ISearchStoreProps {
  filterValues: Partial<IKnmSearchInitFilter>;
  captchaToken: string | null;
  data: IKnmSearchClipped[] | null;
  searchString: string | undefined;
}

interface ISearchForm {
  searchString?: string;
}

export const SearchWithoutStore = ({ filterValues, captchaToken, data, searchString }: ISearchStoreProps) => {
  const dispatch = useDispatch();

  useTitle('Поиск проверок');
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useUnmount(() => {
    dispatch(coreHideNotices());
    dispatch(searchResetPage());
  });

  const [dialog, openDialog] = useState(false);
  const [hasFilters, setHasFilters] = useState(false);
  const [tableColumns, setTableColumns] = useState(initialSearchTableColumns);

  const formCallback = () => {
    if (!captchaToken) {
      dispatch(captchaSetActionAfterTokenLoad(searchGetResultData));
      dispatch(captchaSetIsActive(true));
    } else {
      dispatch(searchGetResultData());
    }
  };

  const handleSortChange = (id: string) => {
    const newColumnsData = tableColumns.map(tableColumnData => {
      if (tableColumnData.id === id) {
        const currentSort = tableColumns.filter(tableColumn => tableColumn.id === id)[0].sort;
        let newSort = '';
        if (currentSort === '') {
          newSort = 'asc';
        } else if (currentSort === 'asc') {
          newSort = 'desc';
        }
        return {
          ...tableColumnData,
          sort: newSort,
        };
      }
      return tableColumnData;
    });
    setTableColumns(newColumnsData);
    dispatch(
      searchSetOrder(
        newColumnsData
          .filter(col => col.sort)
          .map(col => `${col.id},${col.sort}`)
          .join(';'),
      ),
    );
    dispatch(searchGetResultData());
  };

  useEffect(() => {
    const hasSelectedFilters = Object.values(filterValues).reduce(
      (res, el) => res || (typeof el !== 'string' && !Array.isArray(el) && !!el?.value),
      false,
    );
    setHasFilters(hasSelectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, tableColumns]);

  const exportOds = () => dispatch(searchExportData(exportInOds, EXPORT_CHECK_LIST_ODS_FILE_NAME)());

  const exportXlsx = () => dispatch(searchExportData(exportInXlsx, EXPORT_CHECK_LIST_XLSX_FILE_NAME)());

  const options = [
    {
      id: 'inOds',
      content: (
        <span className="shared-dropdown-button" onClick={exportOds}>
          Экспорт списка проверок в ODS
        </span>
      ),
    },
    {
      id: 'inXlsx',
      content: (
        <span className="shared-dropdown-button" onClick={exportXlsx}>
          Экспорт списка проверок в XLSX
        </span>
      ),
    },
  ];

  const onLoadClick = () => {
    if (!captchaToken) {
      dispatch(captchaSetActionAfterTokenLoad(searchGetResultData));
      dispatch(captchaSetIsActive(true));
    } else {
      dispatch(searchGetResultDataNextPage());
    }
  };

  const handleFilterButtonClick = () => openDialog(true);

  const handleChangeSearchString = (e: ChangeEvent<HTMLInputElement>) =>
    dispatch(searchSetSearchString(e.target.value));

  const renderForm = ({ handleSubmit, values }: FormRenderProps<ISearchForm>) => {
    return (
      <form className={styles.Search} onSubmit={handleSubmit}>
        <TextInputField
          className={styles.SearchField}
          type="text"
          name="searchString"
          id="searchInput"
          placeholder="Поиск КНМ"
          validate={composeValidators(
            makeMinCharactersValidator(3, 'Минимальная длина строки не соответствует требуемому значению'),
            makeMaxCharactersValidator(512, 'Превышена допустимая длина строки'),
          )}
          onChange={handleChangeSearchString}
        />
        <Button
          className={styles.SearchButton}
          color="primary"
          type="submit"
          disabled={!(values?.searchString || hasFilters)}
          text="Искать"
        />
        <Tooltip
          content={
            <div className={styles.TooltipWrapper}>
              Доступен поиск КНМ по следующим критериям:
              <ul>
                <li>Учетный номер КНМ</li>
                <li>Номер плана КНМ</li>
                <li>
                  Дата и номер распоряжения или приказа <br /> руководителя органа контроля о проведении КНМ
                </li>
                <li>ИНН или ОГРН проверяемого лица</li>
                <li>Наименование проверяемого лица</li>
                <li>Дата начала проведения КНМ</li>
              </ul>
            </div>
          }
          placement="right"
        >
          <div className={styles.InfoIconBtn} data-tip data-for="infoText" />
        </Tooltip>
      </form>
    );
  };

  return (
    <Layout>
      <div className={styles.PageHolder}>
        <div className={styles.FormWrap}>
          <div className={styles.FormContainer}>
            <div className={styles.FilterWrapper}>
              <Form<ISearchForm> onSubmit={formCallback} render={renderForm} initialValues={{ searchString }} />
              <div className={styles.FilterActionBlock}>
                <Button
                  className={styles.FilterButton}
                  color="primary"
                  onClick={handleFilterButtonClick}
                  text="Фильтры"
                />
                <DropdownButton items={options} disabled={!(data?.length && data?.length > 0)} />
              </div>
            </div>
            <div>
              <SearchFilters filterValues={filterValues} />
            </div>
          </div>
        </div>
        <div className={styles.SearchResultsWrap}>
          <div className={styles.SearchResults}>
            <SearchResults columns={tableColumns} onSortChange={handleSortChange} onLoadClick={onLoadClick} />
          </div>
        </div>
      </div>
      {dialog ? <SearchFiltersModal openDialog={openDialog} dialog={dialog} /> : null}
    </Layout>
  );
};

const mapStateToProps = (state: IRootState): ISearchStoreProps => ({
  filterValues: getSearchFilter(state),
  captchaToken: getCaptchaToken(state),
  data: getSearchResultsData(state),
  searchString: getSearchString(state),
});

export const Search = connect(mapStateToProps)(SearchWithoutStore);
